/**
 * Analytics helpers that mirror the Monolith reputation widget's GA4 tracking.
 *
 * Three event actions are used across the whole widget:
 *   atomic_widget_view   — screen rendered
 *   atomic_widget_click  — button / tile clicked
 *   atomic_widget_submit — form submitted
 *
 * Custom dimensions travel with every event. pp-vue-analytics 1.8.2
 * forwards them as extra event params.
 *   dimension1 — "{practiceId}_{locationId}"  (empty string when no location)
 *   dimension2 — "{practiceId}_{providerId}"  (empty string when no provider)
 *   dimension3 — "desktopWeb" | "mobileWeb" from the user agent, same as the monolith
 *   dimension4 — practiceId
 * PG sends the legacy integer ids. PO sends the practice, location, and provider UUIDs.
 */

// The monolith checks the user agent, not the viewport width.
const device = () => (navigator.userAgent.match('Mobile') == null ? 'desktopWeb' : 'mobileWeb')

function buildDimensions ({ practiceId = '', locationId = '', providerId = '' } = {}) {
  return {
    dimension1: locationId ? `${practiceId}_${locationId}` : '',
    dimension2: providerId ? `${practiceId}_${providerId}` : '',
    dimension3: device(),
    dimension4: practiceId
  }
}

/**
 * @param {Vue} vm        — component instance (provides this.$ppAnalytics)
 * @param {string} label  — event_label value
 * @param {object} dims   — dimensions from buildDimensions()
 */
// pp-vue-analytics reads one object. A (name, params) call never reaches GA.
function track (vm, action, category, label, dims) {
  if (!vm.$ppAnalytics || typeof vm.$ppAnalytics.trackEvent !== 'function') return
  vm.$ppAnalytics.trackEvent({
    action,
    category,
    label,
    ...buildDimensions(dims)
  })
}

export function trackView (vm, label, dims) {
  track(vm, 'atomic_widget_view', 'screen', label, dims)
}

export function trackClick (vm, label, dims) {
  track(vm, 'atomic_widget_click', 'button', label, dims)
}

export function trackSubmit (vm, label, dims) {
  track(vm, 'atomic_widget_submit', 'submit', label, dims)
}

/**
 * Same campaign fields the monolith widget sends to gtag config.
 * utm_source and utm_campaign are not read there: source is always
 * patientpop, and the campaign name is the practice site name.
 * utm_content carries the same share_feedback / likely_refer value the
 * old widget derived from the practice review format.
 */
export function buildCampaignFields ({
  utmMedium,
  utmTerm,
  utmContent,
  practiceName,
  hostname
} = {}) {
  return {
    campaign_source: 'patientpop',
    campaign_name: practiceName || '',
    campaign_content: utmContent != null ? utmContent : 'share_feedback',
    campaign_medium: utmMedium != null ? `quality_survey_${utmMedium}` : 'website_button',
    campaign_keyword: utmTerm != null ? utmTerm : 'share_feedback',
    hostname: hostname || ''
  }
}

export function configureCampaignAnalytics (gtag, fields) {
  if (!gtag || typeof gtag.config !== 'function') return
  gtag.config(buildCampaignFields(fields))
}

export { buildDimensions }
